/**
 * Error telemetry capture for fetch requests.
 *
 * Captures HTTP errors and network failures to Datadog with appropriate
 * context while redacting sensitive information.
 */
import type { ErrorReporter, FetchWrapperConfig } from './types'

/** Regex pattern matching sensitive keys that should be redacted from telemetry */
const REDACT_KEYS =
  /password|passcode|secret|token|auth|cookie|authorization|apikey|api_key|session/i

/**
 * Default URL patterns to ignore for telemetry capture.
 * Includes Datadog ingestion endpoints and app RUM endpoints.
 */
export const DEFAULT_IGNORE_URL_PATTERNS: (string | RegExp)[] = [
  // Datadog RUM & Logs ingestion domains (US1-FED)
  /browser-intake-ddog-gov\.com/i,
  /session-replay\.browser-intake-ddog-gov\.com/i,
  /rum-http-intake/i,
  /\/rum\/intake/i,
  // App's own RUM endpoint(s)
  /\/ingest\/s\//i,
]

/**
 * Recursively redacts sensitive values from an object based on key names.
 *
 * @param value - The value to redact
 * @returns The value with sensitive fields replaced with '[REDACTED]'
 */
export function redactDeep(value: unknown): unknown {
  if (!value || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map(redactDeep)
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, val]) => [
      key,
      REDACT_KEYS.test(key) ? '[REDACTED]' : redactDeep(val),
    ]),
  )
}

/**
 * Formats a URL for safe inclusion in telemetry payloads.
 * Strips query params by default, or redacts values for allowlisted params.
 *
 * @param url - The URL to format
 * @param allowlist - Optional list of query param keys to include (values redacted)
 * @returns A sanitized URL string safe for logging
 */
export function formatUrlForTelemetry(url: URL, allowlist?: string[]): string {
  if (!allowlist || allowlist.length === 0) {
    return `${url.origin}${url.pathname}`
  }
  const inParams = new URLSearchParams(url.search)
  const outParams = new URLSearchParams()
  for (const key of allowlist) {
    if (inParams.has(key)) {
      outParams.set(key, '[REDACTED]')
    }
  }
  const queryString = outParams.toString()
  return `${url.origin}${url.pathname}${queryString ? `?${queryString}` : ''}`
}

/**
 * Checks if a URL should be ignored for telemetry capture.
 *
 * @param url - The URL to check
 * @param customIgnorePatterns - Additional patterns to ignore beyond defaults
 * @returns true if the URL should be skipped for telemetry
 */
export function shouldIgnoreForTelemetry(
  url: URL,
  customIgnorePatterns?: (string | RegExp)[],
): boolean {
  const allPatterns = [
    ...DEFAULT_IGNORE_URL_PATTERNS,
    ...(customIgnorePatterns ?? []),
  ]
  const href = url.href
  return allPatterns.some((rule) =>
    typeof rule === 'string' ? href.includes(rule) : rule.test(href),
  )
}

/**
 * Default status code for error capture when not explicitly configured.
 * 422 is used because it indicates validation errors from our API.
 */
const DEFAULT_CAPTURE_STATUS = 422

/**
 * Determines if an HTTP status code should trigger error capture.
 * Always captures 5xx errors. For other codes, uses configuration.
 *
 * @param status - The HTTP status code
 * @param captureConfig - Configuration for which status codes to capture
 * @returns true if the status should be captured as an error
 */
export function shouldCaptureStatus(
  status: number,
  captureConfig: FetchWrapperConfig['captureStatusCodes'],
): boolean {
  // Always capture server errors
  if (status >= 500) return true

  // Use default if not configured
  if (!captureConfig) return status === DEFAULT_CAPTURE_STATUS

  // Handle string shorthand configurations
  if (captureConfig === '4xx') return status >= 400 && status < 500
  if (captureConfig === '>=400') return status >= 400

  // Handle explicit status code list
  return captureConfig.includes(status)
}

/**
 * Checks if a content-type header indicates JSON content.
 */
function isJsonContentType(contentType: string | null): boolean {
  return (
    !!contentType && /(^|\s|;|,)application\/(.+\+)?json/i.test(contentType)
  )
}

/**
 * Safely parses JSON, returning undefined on failure.
 */
function safeJsonParse(text: string): unknown | undefined {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

/**
 * Captures an HTTP response error with full context for telemetry.
 *
 * @param response - The error response
 * @param request - The original request
 * @param url - The parsed request URL
 * @param config - Fetch wrapper configuration
 * @param reportError - Function to report the error to telemetry
 */
export async function captureResponseError(
  response: Response,
  request: Request,
  url: URL,
  config: Pick<FetchWrapperConfig, 'logUrlParamsAllowlist'>,
  reportError: ErrorReporter,
): Promise<void> {
  const contentType = response.headers.get('content-type')
  let bodyPreviewJson: unknown = undefined
  let bodyPreviewText: string | undefined

  try {
    // Read from a clone so we don't consume the original response body
    const text = await response.clone().text()
    bodyPreviewText = text.slice(0, 2000)

    if (isJsonContentType(contentType)) {
      const parsed = safeJsonParse(text)
      if (parsed !== undefined) {
        const redacted = redactDeep(parsed)
        bodyPreviewJson = redacted
        const stringified = JSON.stringify(redacted, null, 2)
        if (stringified) {
          bodyPreviewText = stringified.slice(0, 2000)
        }
      }
    }
  } catch {
    // Ignore body read errors (opaque/no-cors or stream issues)
  }

  const requestId =
    response.headers.get('x-request-id') ||
    response.headers.get('x-amzn-requestid') ||
    response.headers.get('cf-ray') ||
    undefined

  const safeUrl = formatUrlForTelemetry(url, config.logUrlParamsAllowlist)

  const context: Record<string, unknown> = {
    url: safeUrl,
    httpMethod: request.method,
    httpStatus: response.status,
    httpStatusText: response.statusText,
    pathname: url.pathname,
    contentType,
  }

  if (requestId) context.requestId = requestId
  if (bodyPreviewText !== undefined) context.responseBody = bodyPreviewText
  if (bodyPreviewJson !== undefined) context.responseBodyJson = bodyPreviewJson

  const message = `HTTP ${response.status} for ${request.method} ${url.pathname}`
  reportError(new Error(message), context, message)
}

/**
 * Captures a network error (e.g., CORS, connection failure) for telemetry.
 *
 * @param error - The error that occurred
 * @param request - The original request
 * @param url - The parsed request URL
 * @param config - Fetch wrapper configuration
 * @param reportError - Function to report the error to telemetry
 */
export function captureNetworkError(
  error: unknown,
  request: Request,
  url: URL,
  config: Pick<FetchWrapperConfig, 'logUrlParamsAllowlist'>,
  reportError: ErrorReporter,
): void {
  const safeUrl = formatUrlForTelemetry(url, config.logUrlParamsAllowlist)

  const message =
    error instanceof Error ? error.message : 'Unknown network error in fetch'

  reportError(
    error,
    {
      url: safeUrl,
      httpMethod: request.method,
      pathname: url.pathname,
    },
    message,
  )
}
